import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';
import { Formik, Form } from 'formik';

import ArtImage from 'images/categories/art.svg';
import ComputerScienceImage from 'images/categories/computer-science.svg';
import HealthImage from 'images/categories/health.svg';
import LanguageImage from 'images/categories/language.svg';
import ManagementImage from 'images/categories/management.svg';
import MathematicsImage from 'images/categories/mathematics.svg';
import OtherImage from 'images/categories/other.svg';
import ScienceImage from 'images/categories/science.svg';
import Button from 'uikit/Button';
import Icon from 'uikit/Icon';
import SubPage from 'uikit/SubPage';

import styles from './QuestionPreview.module.scss';

const cx = classNames.bind(styles);

const IMAGES = {
  art: ArtImage,
  'computer-science': ComputerScienceImage,
  health: HealthImage,
  language: LanguageImage,
  management: ManagementImage,
  mathematics: MathematicsImage,
  other: OtherImage,
  science: ScienceImage,
};

const QuestionPreview = ({
  course,
  handleQuestionChange,
  handleQuestionUpdate,
  handleToggleCard,
  initialValues,
  isModalOpen,
  nextQuestion,
  previousQuestion,
  question,
  rectoComponent: RectoQuestion,
  seeRecto,
  t,
  versoComponent: VersoQuestion,
}) => (
  <SubPage isHeaderHidden isOpen={isModalOpen}>
    <div className={cx('subpage')}>
      <div className={cx('content')}>
        <Button icon="arrow-left" intent="white" onClick={handleQuestionUpdate}>
          {t('Course/Shared/QuestionPreview/back')}
        </Button>
        <div className={cx('header')}>
          <div className={cx('icon', `theme-${course.themeBackground}`)}>
            <div
              className={cx('image')}
              style={{
                backgroundImage: `url(${
                  IMAGES[course.category] || IMAGES.other
                })`,
              }}
            />
          </div>
          <div className={cx('information')}>
            <p className={cx('header-title')}>
              {t('Course/Shared/QuestionPreview/title')}
            </p>
            <p className={cx('header-subtitle')}>{course.title}</p>
            <p className={cx('explanation')}>
              {t('Course/Shared/QuestionPreview/explanation')}
            </p>
          </div>
        </div>
        <Formik
          initialValues={initialValues}
          key={question.id}
          onSubmit={() => null}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {() => (
            <Form className={cx('question')}>
              {seeRecto
                ? RectoQuestion && <RectoQuestion question={question} />
                : VersoQuestion && <VersoQuestion question={question} />}
            </Form>
          )}
        </Formik>
      </div>
      <div className={cx('footer')}>
        <Button
          className={cx('left-large')}
          icon="arrow-left"
          isDisabled={!previousQuestion}
          onClick={() => handleQuestionChange(previousQuestion)}
        >
          {t('Course/Shared/QuestionPreview/previous-question')}
        </Button>
        <Button
          className={cx('left-small')}
          icon="arrow-left"
          isDisabled={!previousQuestion}
          onClick={() => handleQuestionChange(previousQuestion)}
          tooltip={t('Course/Shared/QuestionPreview/previous-question')}
        />
        <div className={cx('footer-center')}>
          <Button className={cx('button-center')} onClick={handleToggleCard}>
            {seeRecto
              ? t('Course/Shared/QuestionPreview/see-verso')
              : t('Course/Shared/QuestionPreview/see-recto')}
          </Button>
          <Button
            className={cx('button-center')}
            intent="primary"
            onClick={handleQuestionUpdate}
          >
            {t('Course/shared/QuestionPreview/edit')}
          </Button>
        </div>
        <Button
          className={cx('right-large')}
          isDisabled={!nextQuestion}
          onClick={() => handleQuestionChange(nextQuestion)}
        >
          <span>{t('Course/Shared/QuestionPreview/next-question')}</span>
          <Icon className={cx('right-icon')} name="arrow-right" />
        </Button>
        <Button
          className={cx('right-small')}
          icon="arrow-right"
          isDisabled={!nextQuestion}
          onClick={() => handleQuestionChange(nextQuestion)}
          tooltip={t('Course/Shared/QuestionPreview/next-question')}
        />
      </div>
    </div>
  </SubPage>
);

QuestionPreview.propTypes = {
  course: PropTypes.object.isRequired,
  handleQuestionChange: PropTypes.func.isRequired,
  handleQuestionUpdate: PropTypes.func.isRequired,
  handleToggleCard: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  nextQuestion: PropTypes.object,
  previousQuestion: PropTypes.object,
  question: PropTypes.object,
  rectoComponent: PropTypes.elementType,
  seeRecto: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  versoComponent: PropTypes.elementType,
};

QuestionPreview.defaultProps = {
  nextQuestion: undefined,
  previousQuestion: undefined,
  question: undefined,
  rectoComponent: undefined,
  versoComponent: undefined,
};

export default QuestionPreview;
